/**
 * NotificationBell — the top-right in-app notification bell (Part D).
 *
 * Wires GET /notifications (list + unread_count) to the header bell: an unread badge
 * (count) over the Bell icon + a click-open dropdown listing the caller's notifications.
 * Extensible by `type`: the renderer switches on notification.type; unknown types degrade
 * to a generic row. Marking read (one / all) invalidates the list. In mock/dev mode the
 * hook resolves the feature's own fixture (`./mock`, no network) so the shell renders
 * standalone with one real notification instead of an empty dropdown.
 *
 * No dropdown UI primitive exists in this app — this is a small custom popover with a
 * click-outside + Escape close (the ActionsMenu precedent).
 *
 * [Source: apps/web/src/features/administration/useEffectivePermissions.ts] (live/mock query shape)
 * [Source: apps/web/src/lib/api-client.ts#notifications]
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check } from 'lucide-react';
import type { NotificationDto, NotificationsResponse } from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { cn } from '../../lib/cn';
import { NOTIFICATIONS_MOCK } from './mock';
import { KOMMUNIKATION_ENABLED } from '../../lib/feature-flags';

const EMPTY: NotificationsResponse = { notifications: [], unread_count: 0 };

/** Fallback target of a Freigabe-Anfrage entry: the Freigaben tab — on Aufgaben while Kommunikation
 *  is off (Jevin #37: the removed /kommunikation route used to redirect ?tab=freigaben → /aufgaben). */
const FREIGABEN_TAB_LINK = KOMMUNIKATION_ENABLED ? '/kommunikation?tab=freigaben' : '/aufgaben';

/** Fallback target of a mail-team entry: Kommunikation, or the Posteingang list while it is off. */
const KOMMUNIKATION_LINK = KOMMUNIKATION_ENABLED ? '/kommunikation' : '/posteingang';

/** While Kommunikation is switched off, a mail entry opens its Eingang (derived from comm_id), and
 *  any link that still names /kommunikation (an older stored deep_link) is rewritten to the live
 *  Posteingang so it never 404s on the removed route. */
function mailLink(n: NotificationDto, kommunikationLink: string): string {
  const commId = n.payload['comm_id'];
  if (!KOMMUNIKATION_ENABLED) {
    if (typeof commId === 'string' && commId) return `/posteingang/${commId}`;
    return kommunikationLink.startsWith('/kommunikation') ? '/posteingang' : kommunikationLink;
  }
  return kommunikationLink;
}

/** Coerce a payload `stufe` to a valid Kontrollbereich Fristenstufe (1–4), else null. */
function normalizeStufe(raw: unknown): 1 | 2 | 3 | 4 | null {
  const n = Number(raw);
  return n === 1 || n === 2 || n === 3 || n === 4 ? (n as 1 | 2 | 3 | 4) : null;
}

/** Human-readable German summary for a notification (extensible by type). Exported for unit tests. */
export function describeNotification(n: NotificationDto): {
  title: string;
  body?: string;
  deepLink: string | null;
} {
  if (n.type === 'access_request') {
    const name = String(n.payload['requester_name'] ?? 'Ein Benutzer');
    const link = typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : null;
    return { title: `${name} beantragt erweiterten Zugriff`, deepLink: link };
  }
  if (n.type === 'role_assigned') {
    const roleName = String(n.payload['role_name'] ?? '');
    const link = typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : null;
    return { title: `Ihnen wurde die Rolle "${roleName}" zugewiesen`, deepLink: link };
  }
  if (n.type === 'process_reminder') {
    const level = Number(n.payload['level'] ?? 0);
    const link = typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : null;
    const title = level > 0 ? `Offener Vorgang: ${level}. Erinnerung` : 'Ein Vorgang wartet auf Bearbeitung';
    return { title, deepLink: link };
  }
  // Kontrollbereich Zuweisung (Bereich 5): an element was routed to the caller / their group.
  // Payload (ids only, rule #6): backlog_item_id, element_id, task_id, zuweisung_typ, hat_frist, stufe.
  if (n.type === 'control_backlog_assignment') {
    const itemId = typeof n.payload['backlog_item_id'] === 'string' ? n.payload['backlog_item_id'] : null;
    const stufe = normalizeStufe(n.payload['stufe']) ?? 1;
    const toGroup = n.payload['zuweisung_typ'] === 'gruppe';
    const hatFrist = n.payload['hat_frist'] === true;
    const body = `Ein Element aus dem Kontrollbereich wurde ${toGroup ? 'Ihrer Gruppe' : 'Ihnen'} zugewiesen${
      hatFrist ? ' (mit Frist)' : ''
    }.`;
    // Deep-link to the element's Kontrollbereich detail; fall back to the stage list if the id is missing.
    const deepLink = itemId ? `/portfolio/kontrolle/${stufe}/${itemId}` : `/portfolio/kontrolle/${stufe}`;
    return { title: 'Rückstand zugewiesen', body, deepLink };
  }
  // Kontrollbereich Eskalation (Bereich 5): an element reached the top Fristenstufe (Stufe 4).
  // Payload (ids only, rule #6): backlog_item_id, element_typ, element_id, stufe.
  if (n.type === 'control_backlog_escalation') {
    const itemId = typeof n.payload['backlog_item_id'] === 'string' ? n.payload['backlog_item_id'] : null;
    const stufe = normalizeStufe(n.payload['stufe']) ?? 4;
    const body = 'Ein Element im Kontrollbereich hat die höchste Fristenstufe erreicht und wurde eskaliert.';
    const deepLink = itemId ? `/portfolio/kontrolle/${stufe}/${itemId}` : `/portfolio/kontrolle/${stufe}`;
    return { title: `Eskalation Stufe ${stufe}`, body, deepLink };
  }
  // Freigabe-Anfrage auf EINE E-Mail (Berechtigungskonzept V4.0, Bereich 1, 3.3): a
  // reduced-tier colleague asks a participant for Vollzugriff. Payload (ids + label only,
  // rule #6): request_id, comm_id, requester_user_id, requester_name, target_role, deep_link.
  // The deep link carries a query string; onRowClick splits it before navigating.
  if (n.type === 'comm_access_requested') {
    const name = String(n.payload['requester_name'] ?? 'Ein Benutzer');
    const link =
      typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : FREIGABEN_TAB_LINK;
    return {
      title: 'Freigabe-Anfrage für eine E-Mail',
      body: `${name} bittet um Vollzugriff.`,
      deepLink: mailLink(n, link),
    };
  }
  // Befristete Freigabe abgelaufen (Bereich 1, 3.3). Recipient = the person whose grant
  // ended, so the link goes to Kommunikation (where the mail is and a new request starts),
  // NOT to the Freigaben tab, which is the decider inbox and would show them nothing.
  // Payload (ids only): grant_id, comm_id.
  if (n.type === 'freigabe_expired') {
    return {
      title: 'Freigabe abgelaufen',
      body: 'Ihre befristete Freigabe für eine E-Mail ist abgelaufen. Fragen Sie bei Bedarf eine neue Freigabe an.',
      deepLink: mailLink(n, '/kommunikation'),
    };
  }
  // MV (mail-only team visibility): post arrived for a client whose mail is restricted to a
  // team. Payload (ids only, rule #6): comm_id, subject_type, subject_id, team_id, deep_link.
  // NOTHING from the mail is rendered (no subject, no sender, no client name), so the bell is
  // not a side channel around the read filter the recipient still passes in Kommunikation.
  if (n.type === 'sensitive_mail_arrived') {
    const link =
      typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : KOMMUNIKATION_LINK;
    return {
      title: 'Neue Post für einen vertraulichen Kunden',
      body: 'Für einen Kunden, dessen Post nur Ihr Team sieht, ist eine E-Mail eingetroffen.',
      deepLink: mailLink(n, link),
    };
  }
  // MV: mail that could not be matched to any client, so the tenant's default mail team is
  // the only one who sees it. Payload (ids only, rule #6): comm_id, team_id, deep_link.
  // Same confidentiality as above: nothing from the mail itself is rendered.
  if (n.type === 'unmatched_mail_arrived') {
    const link =
      typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : KOMMUNIKATION_LINK;
    return {
      title: 'Neue nicht zugeordnete Post',
      body: 'Eine E-Mail ohne Kundenzuordnung ist eingetroffen. Sie ist nur für Ihr Team sichtbar.',
      deepLink: mailLink(n, link),
    };
  }
  // An overdue Freigabe whose process has no Funktionsgruppe routing: the default team is
  // asked to pick it up. Payload (ids + the process key only): task_id, process, team_id, deep_link.
  if (n.type === 'approval_unrouted_overdue') {
    const link = typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : '/aufgaben';
    return {
      title: 'Freigabe überfällig, keine Funktionsgruppe zuständig',
      body: 'Eine Aufgabe wartet seit über 24 Stunden auf ihre Freigabe und kann keiner Funktionsgruppe zugewiesen werden.',
      deepLink: link,
    };
  }
  // MV: a restriction was switched ON for mail that already existed. ONE summary carrying the
  // COUNT (payload, ids + a count only: team_id, mail_count, subject_type, subject_id,
  // tenant_default, deep_link) - never one row per mail.
  if (n.type === 'mails_restricted_to_team') {
    // The api suppresses this bell entirely at a count of 0, so «0 E-Mails …» would
    // announce that nothing happened. A count that is missing or unreadable must not become
    // that sentence, nor «NaN E-Mails»: the row then says the same thing without a number.
    const rawCount = Number(n.payload['mail_count']);
    const count = Number.isInteger(rawCount) && rawCount > 0 ? rawCount : null;
    const link =
      typeof n.payload['deep_link'] === 'string' ? n.payload['deep_link'] : KOMMUNIKATION_LINK;
    const body =
      count === null
        ? 'Post ist ab sofort nur für Ihr Team sichtbar.'
        : count === 1
          ? '1 E-Mail ist ab sofort nur für Ihr Team sichtbar.'
          : `${count} E-Mails sind ab sofort nur für Ihr Team sichtbar.`;
    return { title: 'Post neu auf Ihr Team beschränkt', body, deepLink: link };
  }
  return { title: 'Neue Benachrichtigung', deepLink: null };
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' });
}

export function NotificationBell() {
  const live = isLiveData();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const query = useQuery<NotificationsResponse>({
    queryKey: qk.notifications(),
    queryFn: () => (live ? apiClient.notifications.list() : Promise.resolve(NOTIFICATIONS_MOCK)),
    // Poll while live so the badge stays current without a manual refresh.
    refetchInterval: live ? 60_000 : false,
    staleTime: live ? 30_000 : Infinity,
    ...(live ? {} : { initialData: NOTIFICATIONS_MOCK }),
  });

  const data = query.data ?? EMPTY;
  const unread = data.unread_count;

  const markRead = useMutation({
    mutationFn: (id: string) => apiClient.notifications.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications() }),
  });
  const markAll = useMutation({
    mutationFn: () => apiClient.notifications.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications() }),
  });

  // Close on click-outside + Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function onRowClick(n: NotificationDto) {
    if (n.read_at === null) markRead.mutate(n.id);
    const { deepLink } = describeNotification(n);
    if (deepLink) {
      setOpen(false);
      // TanStack Router takes the path and the search params as TWO keys, a bare
      // '/pfad?a=b' string never resolves (features/invoices/task-link.ts is the repo's
      // only working form). Split the query string off so a payload deep-link with search
      // params lands where it says, not on the bare path.
      const [path = deepLink, queryString] = deepLink.split('?');
      const search = queryString
        ? (Object.fromEntries(new URLSearchParams(queryString)) as Record<string, string>)
        : undefined;
      void navigate(search ? { to: path, search } : { to: path });
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={unread > 0 ? `Benachrichtigungen (${unread} ungelesen)` : 'Benachrichtigungen'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="relative grid size-8 place-items-center rounded-md hover:bg-accent"
      >
        <Bell className="size-4 text-muted-foreground" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-sem-alert px-1 text-[10px] font-semibold leading-4 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-10 z-50 w-80 overflow-hidden rounded-lg border border-border bg-popover shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-semibold">Benachrichtigungen</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <Check className="size-3" />
                Alle gelesen
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                Keine Benachrichtigungen.
              </p>
            ) : (
              data.notifications.map((n) => {
                const { title, body } = describeNotification(n);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => onRowClick(n)}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 border-b border-border/60 px-3 py-2 text-left hover:bg-accent',
                      n.read_at === null && 'bg-primary/5',
                    )}
                  >
                    <span className="flex w-full items-start gap-2">
                      {n.read_at === null && (
                        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-sem-alert" />
                      )}
                      <span className="flex-1 text-sm text-foreground">{title}</span>
                    </span>
                    {body && <span className="pl-3.5 text-xs text-muted-foreground">{body}</span>}
                    <span className="pl-3.5 text-[11px] text-muted-foreground">
                      {formatWhen(n.created_at)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
